<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useCompanyStore } from '@Stores/company.store';
import { useRequestSessionStore } from '@Stores/requestSession.store';
import { getIdvStatusApi } from '@Api/idv.api';
import QrDisplay from './QrDisplay.vue';

const POLL_INTERVAL_MS = 5000;
const MAX_CONSECUTIVE_FAILURES = 3;

const router = useRouter();
const companyStore = useCompanyStore();
const requestSessionStore = useRequestSessionStore();

const qrCodeUrl = ref<string | null>(null);

const buildQrCodeUrl = (qrSessionToken?: string): string | null => {
    const token = requestSessionStore.inviteToken;
    if (!token) {
        console.error('No invite token found. Cannot build QR code URL.');
        return null;
    }
    const query: Record<string, string> = { utm_source: 'qrcode' };
    if (qrSessionToken) query.qrSessionToken = qrSessionToken;

    const resolved = router.resolve({
        name: 'match-idv-select-doc-type',
        params: { requestType: 'identity', token },
        query,
    });
    return new URL(resolved.href, globalThis.location.origin).href;
};

let pollTimeoutId: ReturnType<typeof setTimeout> | null = null;
let consecutiveFailures = 0;

const pollIdvStatus = async () => {
    const status = await getIdvStatusApi();

    if (status?.isComplete) {
        router.push({ name: 'match-idv-processing-complete' });
        return;
    }

    if (!status) {
        consecutiveFailures++;
        if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
            console.error(`IDV status polling failed ${MAX_CONSECUTIVE_FAILURES} consecutive times. Navigating to error page.`);
            router.push({ name: 'general-error' });
            return;
        }
    } else {
        consecutiveFailures = 0;
    }

    pollTimeoutId = setTimeout(pollIdvStatus, POLL_INTERVAL_MS);
};

onMounted(() => {
    const token = requestSessionStore.qrSessionToken ?? undefined;
    if (!token) {
        console.warn('[Request MFE] qrSessionToken not found in store — QR code may be incomplete.');
    }
    qrCodeUrl.value = buildQrCodeUrl(token);
    pollIdvStatus();
});

onUnmounted(() => {
    if (pollTimeoutId) {
        clearTimeout(pollTimeoutId);
        pollTimeoutId = null;
    }
});
</script>

<template>
    <div class="component-wrapper">
        <p class="qr-code-message">
            <strong>{{ companyStore.company?.name }}</strong> is using CertifID to verify your identity.
        </p>
        <p>
            To continue, scan this QR code with your phone camera.
        </p>
        <qr-display v-if="qrCodeUrl" :url="qrCodeUrl" class="qr-display" />
    </div>
</template>

<style lang="scss" scoped>
.component-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.qr-code-message {
    line-height: 150%;
}

.qr-display {
    margin-top: 1.5rem;
}


</style>
